import {
  Card,
  FlexBox,
  GridContainer,
  Row,
  Typography,
  responsive,
  Column,
  Spinner,
  AlertBox,
  Box,
} from '@vp/swan';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import { useMemo, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { pageViewBuyItAgain } from '../../apis/trackingApi';
import { usePaginationContext } from '../contexts/PaginationContext';
import { GET_BUY_IT_AGAIN } from '../../constants/reactQueryKeys';
import { getReorderableItems } from '../../apis/bopApi';
import { useSiteData } from '../../hooks/useSiteData';
import { useShopperId } from '../../hooks/useShopperId';
import {
  trackBuyItAgainPresentedToCustomer,
  trackShopperHasNoBuyItAgainItems,
} from '../../apis/newrelic/newRelicTrackingApi';
import { trackFetchBuyItAgainItemsFail } from '../../apis/newrelic/bopNewRelicTrackingApi';
import { ErrorHandler, isErrorObj } from '../shared/ErrorHandler';
import { BUY_IT_AGAIN_SOURCE_PAGE } from '../../constants/sourcePage';
import { BuyItAgainLineItemDisplay } from './BuyItAgainLineItemDisplay';
import { LineItemDataProvider } from '../contexts/LineItemDataContext';
import { PaginationWithEllipses } from '../pagination/PaginationWithEllipses';
import { EmbeddableLinkCreator } from '../shared/EmbeddableLinkCreator';
import { ORDER_HISTORY_TAB } from '../../constants/orderHistoryTabs';
/**
 * Renders items from BOP re-orderable Items fetch to display on "Buy It Again" page. Each item is wrapped
 * in a LineItemDataProvider so shared components (namely within the reorder flow) still function.
 * Components specific to "Buy It Again" do not use LineItemDataProvider data and instead use the BOP item contract.
 */

interface BuyItAgainViewProps {
  handleRequestTabChange: (tabId: string) => void;
}

export const BuyItAgainView = ({
  handleRequestTabChange,
}: BuyItAgainViewProps) => {
  const {
    vistacartTenant: vistacartTenantName,
    tenantEnvironment,
    fragmentName,
  } = useSiteData();
  const shopperId = useShopperId();

  const analyticsMetadata = useMemo(
    () => ({
      source: BUY_IT_AGAIN_SOURCE_PAGE,
      siteName: fragmentName,
      tenant: vistacartTenantName,
      shopperId,
    }),
    [fragmentName, vistacartTenantName, shopperId]
  );

  const [buyItAgainMessages = {}, sharedActionMessages = {}] = useTranslations([
    'buyItAgainMessages',
    'sharedActionMessages',
  ]);

  const {
    currentPageIndex,
    setTotalItemCount,
    numberOfItemsPerPage,
    totalPageCount,
  } = usePaginationContext();

  // Fetches the shopper's reorderable items, if any
  const { isLoading, isError, error, data, isSuccess, refetch } = useQuery({
    queryKey: [
      GET_BUY_IT_AGAIN,
      currentPageIndex,
      numberOfItemsPerPage,
      shopperId,
      vistacartTenantName,
      tenantEnvironment,
    ],

    queryFn: () =>
      getReorderableItems({
        shopperId,
        environment: tenantEnvironment,
        vistacartTenantName,
        page: currentPageIndex,
        pageSize: numberOfItemsPerPage,
      }),

    /**
     * This prevents our current query data from being immediately invalidated on every refetch for a new page,
     * making page loads seamless. Read about it here: https://tanstack.com/query/latest/docs/framework/react/guides/paginated-queries
     */
    placeholderData: keepPreviousData,
  });

  // refetch buyItAgain items on retry link click
  const refetchBuyItAgain = () => {
    refetch();
  };

  // refetch buyItAgain items on retry link click
  const navigateToBuyItAgainTab = () => {
    handleRequestTabChange(ORDER_HISTORY_TAB);
  };

  useEffect(() => {
    if (isSuccess) {
      setTotalItemCount(data?.total);
    }
  }, [data, isSuccess, setTotalItemCount]);

  const items = data?.items || [];
  const shopperHasReorderableItems = items.length > 0;

  // Fire a New Relic event for the "Buy It Again" UX on render
  useEffect(() => {
    trackBuyItAgainPresentedToCustomer(analyticsMetadata, totalPageCount);
  }, [analyticsMetadata, shopperId, totalPageCount]);

  // Fire a Segment page viewed event on page load
  useEffect(() => {
    pageViewBuyItAgain(shopperId);
  }, [shopperId]);

  // If the fetch fails, fire a NR event, show error message
  if (isError) {
    if (error instanceof Error) {
      trackFetchBuyItAgainItemsFail(error, analyticsMetadata);
    }

    const isRetryableError = isErrorObj(error) ? error.isRetryable : false;

    if (isRetryableError) {
      return (
        <Box mx={4} mb={10}>
          <AlertBox skin="warning">
            <ReplaceTokens
              tokenizedString={buyItAgainMessages.retryableError}
              substitutions={{
                RetryLink: EmbeddableLinkCreator({
                  onClick: refetchBuyItAgain,
                }),
                OrderHistoryLink: EmbeddableLinkCreator({
                  onClick: navigateToBuyItAgainTab,
                }),
              }}
            />
          </AlertBox>
        </Box>
      );
    }

    // For non-retryable errors, use the ErrorHandler component
    return <ErrorHandler error={error} />;
  }

  // While the fetch is loading, show a preloader
  if (isLoading) {
    return (
      <FlexBox justifyContent="center">
        <Spinner
          accessibleText={sharedActionMessages['loading']}
          aria-busy="true"
          aria-live="polite"
          role="status"
          size="super"
        />
      </FlexBox>
    );
  }

  // If the shopper has no items, fire a NR event and show a message
  if (!shopperHasReorderableItems) {
    trackShopperHasNoBuyItAgainItems(analyticsMetadata);
    return (
      <Card backgroundColor="strong" mx={4} mb={10} style={{ width: '100%' }}>
        <FlexBox
          style={{ height: 300 }}
          justifyContent="center"
          alignItems="center"
        >
          <Typography fontWeight="bold">
            {buyItAgainMessages.emptySetMessage}
          </Typography>
        </FlexBox>
      </Card>
    );
  }
  const ResponsiveColumn = responsive(Column);

  return (
    <>
      <GridContainer>
        <Row component="ul">
          {items.map((item, index) => (
            <ResponsiveColumn
              component="li"
              span={12}
              key={`buy-it-again-item-${index}`}
              pt={{ xs: 5, sm: 7 }}
              xs={{ span: 12 }}
              sm={{ span: 6 }}
              md={{ span: 4 }}
              lg={{ span: 4 }}
            >
              <LineItemDataProvider item={item} orderNumber={item.orderNumber}>
                <BuyItAgainLineItemDisplay
                  item={item}
                  displayPosition={index + 1}
                />
              </LineItemDataProvider>
            </ResponsiveColumn>
          ))}
        </Row>
      </GridContainer>
      {totalPageCount > 1 && <PaginationWithEllipses />}
    </>
  );
};
